import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

/**
 * Generic Player's Locker KPI history page.
 * Props: kpi, eyebrow, icon, apiPath, newPath
 */
const KpiHistoryPage = ({ kpi, eyebrow, icon: Icon, apiPath, newPath }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const resp = await API.get(apiPath);
      setSessions(resp.data);
    } catch {
      toast.error('Failed to load sessions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [apiPath]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this round? This cannot be undone.')) return;
    try {
      await API.delete(`${apiPath}/${id}`);
      toast.success('Round deleted.');
      load();
    } catch {
      toast.error('Failed to delete.');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid={`${kpi}-history-page`}>
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-4xl flex items-center justify-between">
          <Link to="/locker" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-locker">
            <ArrowLeft className="w-4 h-4" /> Locker
          </Link>
          <Link
            to={newPath}
            className="inline-flex items-center gap-2 bg-[#BC562B] text-white px-4 py-2 text-sm font-semibold hover:bg-[#9A4520] transition-colors"
            data-testid={`${kpi}-new-log-btn`}
          >
            <Plus className="w-4 h-4" /> New round
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-4xl">
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4" />} {eyebrow}
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">All your rounds</h1>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#E7E5E4]" data-testid={`${kpi}-empty-history`}>
            <Calendar className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
            <p className="text-[#737373] mb-4">No rounds logged yet</p>
            <Link to={newPath} className="inline-flex items-center gap-2 bg-[#2C5530] text-white px-5 py-2.5 font-semibold hover:bg-[#1E3A21] transition-colors">
              <Plus className="w-4 h-4" /> Log your first round
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {sessions.map((s) => {
              const open = expanded === s.id;
              return (
                <li key={s.id} className="bg-white border border-[#E7E5E4]" data-testid={`${kpi}-history-row-${s.id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : s.id)}
                    className="w-full text-left p-5 flex items-center justify-between hover:bg-[#FAFAF9] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-[#1A1A1A]">{s.course_name || 'Round'}</span>
                        {s.round_type && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#F2F0E9] text-[#57534E] px-2 py-0.5">{s.round_type}</span>
                        )}
                        <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#1A2E1D]/10 text-[#1A2E1D] px-2 py-0.5">{s.mode}</span>
                      </div>
                      <p className="text-xs text-[#737373]">
                        {new Date(s.round_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        {s.score != null && ` · Score ${s.score}`}
                      </p>
                    </div>
                    <span className="bg-[#1A2E1D] text-white text-sm font-semibold px-3 py-1.5 ml-3 flex-shrink-0">
                      {s.overall}/10
                    </span>
                  </button>
                  {open && (
                    <div className="px-5 pb-5 pt-1 space-y-3 border-t border-[#F2F0E9]">
                      {s.sub_scores && (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
                          {Object.entries(s.sub_scores).map(([k, v]) => v != null && (
                            <div key={k} className="text-center p-2 bg-[#FAFAF9] border border-[#F2F0E9]">
                              <p className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">{k.replace(/_/g, ' ')}</p>
                              <p className="font-['Playfair_Display'] text-lg text-[#1A2E1D]">{v}<span className="text-xs text-[#737373]">/10</span></p>
                            </div>
                          ))}
                        </div>
                      )}
                      {s.notable_moments && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">Notable moments</p>
                          <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{s.notable_moments}</p>
                        </div>
                      )}
                      {s.reflection && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">Reflection</p>
                          <p className="text-sm text-[#57534E] whitespace-pre-wrap">{s.reflection}</p>
                        </div>
                      )}
                      <div className="pt-2">
                        <button
                          onClick={() => handleDelete(s.id)}
                          className="inline-flex items-center gap-1.5 text-xs text-[#BC562B] hover:text-[#9A4520]"
                          data-testid={`${kpi}-delete-${s.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};

export default KpiHistoryPage;
